import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import CloseButton from "react-bootstrap/CloseButton";
import "./style.scss";
import CommonButton from "../../commonButton";

const SignatureSettingsModal = ({
  show,
  onHide,
  field = null,
  updateField,
  onProvideESignatureClick,
}) => {
  const [signatureData, setSignatureData] = useState({
    signerType: "",
    required: false,
    fontSize: "",
    fontStyle: "",
  });

  const [errors, setErrors] = useState({});
  const [hasESignature, setHasESignature] = useState(false);

  useEffect(() => {
    if (field) {
      // Parse existing signature data if it exists
      try {
        const parsed =
          typeof field.value === "string"
            ? JSON.parse(field.value)
            : field.value;
        setSignatureData({
          signerType: parsed.signerType || "",
          required:
            field.needed !== undefined
              ? field.needed
              : parsed.required || false,
          fontSize: field.fontSize || parsed.fontSize || "",
          fontStyle: field.fontStyle || parsed.fontStyle || "",
        });
        // Check if e-signature already exists and signerType is "Mine"
        setHasESignature(
          parsed.signerType === "Mine" &&
            (!!parsed.signature || !!field.signature),
        );
      } catch (e) {
        setSignatureData({
          signerType: "",
          required: field.needed || false,
          fontSize: field.fontSize || "",
          fontStyle: field.fontStyle || "",
        });
        setHasESignature(false);
      }
    }
  }, [field]);

  const handleInputChange = (field, value) => {
    setSignatureData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDone = () => {
    const newErrors = {};

    // Validate signature type
    if (!signatureData.signerType) {
      newErrors.signerType = "This field is required";
    }

    // Validate e-signature if signer type is "Mine"
    if (signatureData.signerType === "Mine" && !hasESignature) {
      newErrors.eSignature = "Please provide e-signature";
    }

    // If there are errors, set them and stop
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({});

    if (typeof updateField === "function") {
      // Get existing signature data to preserve signature if needed
      let existingData = {};
      if (field && field.value) {
        try {
          existingData =
            typeof field.value === "string" && field.value.startsWith("{")
              ? JSON.parse(field.value)
              : field.value;
        } catch (e) {
          existingData = {};
        }
      }

      // If changing to Employee, remove the signature image
      const finalSignatureData = {
        ...signatureData,
        // Only preserve signature if signerType is "Mine"
        signature:
          signatureData.signerType === "Mine"
            ? existingData.signature
            : undefined,
        fullName:
          signatureData.signerType === "Mine"
            ? existingData.fullName
            : undefined,
      };

      const updatedField = {
        ...field,
        value: JSON.stringify(finalSignatureData),
        needed: signatureData.required,
        fontSize: signatureData.fontSize,
        fontStyle: signatureData.fontStyle,
      };
      updateField(updatedField);
    }

    if (typeof onHide === "function") {
      onHide();
    }
  };

  return (
    <Modal
      show={show}
      centered
      onHide={onHide}
      className="common-popup signature-settings-modal"
    >
      <Modal.Header className="settings-modal-header">
        <div className="form-modal">
          <Modal.Title>Signature Settings</Modal.Title>
        </div>
        <CloseButton onClick={onHide} className="close-cta-wrapper" />
      </Modal.Header>
      <Modal.Body>
        <div className="signature-settings-form">
          <div className="form-group">
            <label htmlFor="signerType">Signature Type</label>
            <div className="form-control-wrapper">
              <select
                id="signerType"
                className={`form-control ${errors.signerType ? "is-invalid" : ""}`}
                name="signerType"
                value={signatureData.signerType || ""}
                onChange={(e) => {
                  handleInputChange("signerType", e.target.value);
                  // Clear error when user starts typing
                  if (errors.signerType) {
                    setErrors((prev) => ({ ...prev, signerType: "" }));
                  }
                  // Clear e-signature error when changing signer type
                  if (errors.eSignature) {
                    setErrors((prev) => ({ ...prev, eSignature: "" }));
                  }
                  // Reset e-signature status when changing from "Mine"
                  if (e.target.value !== "Mine") {
                    setHasESignature(false);
                  }
                }}
                style={{
                  MozAppearance: "number-input",
                  WebkitAppearance: "auto",
                  appearance: "auto",
                }}
              >
                <option value="">Select Signature Type</option>
                <option value="Mine">Mine</option>
                <option value="Employee">Employee</option>
              </select>
              {errors.signerType && (
                <div className="invalid-feedback">{errors.signerType}</div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="fontSize">Font Size</label>
            <input
              type="number"
              id="fontSize"
              className="form-control"
              name="fontSize"
              value={signatureData.fontSize || ""}
              onChange={(e) => {
                handleInputChange("fontSize", e.target.value);
              }}
              placeholder="Enter font size"
              min="8"
              max="72"
            />
          </div>

          <div className="form-group">
            <label htmlFor="fontStyle">Font Family</label>
            <select
              id="fontStyle"
              className="form-control"
              name="fontStyle"
              value={signatureData.fontStyle || ""}
              onChange={(e) => {
                handleInputChange("fontStyle", e.target.value);
              }}
              style={{
                MozAppearance: "number-input",
                WebkitAppearance: "auto",
                appearance: "auto",
              }}
            >
              <option value="">Select Font Family</option>
              <option value="Arial">Arial</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Courier New">Courier New</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
              <option value="Helvetica">Helvetica</option>
            </select>
          </div>

          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="isRequired"
              name="isRequired"
              checked={signatureData.required || false}
              onChange={(e) => {
                handleInputChange("required", e.target.checked);
              }}
            />
            <label className="checkbox-label" htmlFor="isRequired">
              Field is required
            </label>
          </div>

          {signatureData.signerType === "Mine" && (
            <div className="form-group e-signature-button-wrapper">
              <CommonButton
                onClick={() => {
                  // Call the parent function to open e-signature modal
                  if (typeof onProvideESignatureClick === "function") {
                    // Pass the current signerType to ensure it's preserved
                    onProvideESignatureClick(field, signatureData.signerType);
                    // Assume signature is provided after clicking
                    setHasESignature(true);
                    // Clear e-signature error if exists
                    if (errors.eSignature) {
                      setErrors((prev) => ({ ...prev, eSignature: "" }));
                    }
                  }
                }}
                variant="secondary"
                btnText="Click to provide e-signature"
              />
              {errors.eSignature && (
                <div className="invalid-feedback signature">
                  {errors.eSignature}
                </div>
              )}
            </div>
          )}

          <div className="update-button-wrapper left-aligned">
            <CommonButton
              onClick={handleDone}
              variant="primary"
              btnText="DONE"
            />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SignatureSettingsModal;
